import { Inject, Injectable, Type } from '@angular/core';
import { Observable, Subject, Subscriber } from 'rxjs';
import {
  IDialogComponent,
  IDialogData,
} from '../../components/dialog-templates/dialog.data';
import { shareReplay } from 'rxjs/operators';
import { IconSizesEnum } from '../../components/icon/icon.component';
import {
  DIALOG_BASIC_COMPONENT,
  DIALOG_LOADING_COMPONENT,
} from '../../components/dialog-templates/dialog.tokens';
import { I2gLayoutService } from '../layout/i2g-layout.service';

@Injectable({
  providedIn: 'root',
})
export class I2gDialogService {
  private _activeSubscriber: Subscriber<IDialogData>;
  private _dialogSubject: Subject<IDialogData> = new Subject();
  dialog$: Observable<IDialogData> = this._dialogSubject.asObservable();

  constructor(
    @Inject(DIALOG_BASIC_COMPONENT) private _dialogBasicComponent: Type<any>,
    @Inject(DIALOG_LOADING_COMPONENT)
    private _dialogLoadingComponent: Type<any>,
    private _layoutService: I2gLayoutService
  ) {}

  openDialog(component: Type<any>, props: any = {}): Observable<any> {
    this._layoutService.disableBodyScrolling();

    const observable = new Observable((subscriber) => {
      this._activeSubscriber = subscriber;
      this._dialogSubject.next({
        component: <IDialogComponent>component,
        props,
      });
    }).pipe(shareReplay(1));

    observable.subscribe();

    return observable;
  }

  closeDialog(result: any = null): void {
    this._layoutService.enableBodyScrolling();

    if (!this._activeSubscriber) {
      return;
    }

    this._activeSubscriber.next(result);
    this._activeSubscriber.complete();
    this._dialogSubject.next(null);
  }

  openAlertDialog(props: any = {}): Observable<any> {
    return this.openDialog(this._dialogBasicComponent, {
      icon: {
        name: 'alert-xlarge',
        size: IconSizesEnum.x_large,
      },
      isDismissible: true,
      ...props,
    });
  }

  openInfoDialog(props: any = {}): Observable<any> {
    return this.openDialog(this._dialogBasicComponent, {
      icon: {
        name: 'info-xlarge',
        size: IconSizesEnum.x_large,
      },
      isDismissible: true,
      ...props,
    });
  }

  openSuccessDialog(props: any = {}): Observable<any> {
    return this.openDialog(this._dialogBasicComponent, {
      icon: {
        name: 'success-xlarge',
        size: IconSizesEnum.x_large,
      },
      isDismissible: true,
      ...props,
    });
  }

  openLoadingDialog(): Observable<any> {
    return this.openDialog(this._dialogLoadingComponent);
  }
}
